import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useBatchUploadContext } from "../../context/BatchUploadContext";
import { FaCheck, FaChevronRight, FaSpinner, FaUpload } from "../../utils/icons";
import { batchMonitorSummary } from "./batchUploadSummary";

export { batchMonitorSummary } from "./batchUploadSummary";

export const BatchUploadMonitor: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { batch } = useBatchUploadContext();
  const summary = useMemo(
    () => batchMonitorSummary(batch.phase, batch.cases),
    [batch.cases, batch.phase],
  );

  if (batch.phase === "idle" || location.pathname === "/upload") return null;
  const isDone = batch.phase === "done";

  return (
    <button
      type="button"
      aria-label={`View batch upload progress: ${summary.label}, ${summary.detail}`}
      data-testid="batch-upload-monitor"
      onClick={() => navigate("/upload")}
      className="group fixed bottom-16 right-4 z-40 w-[min(19rem,calc(100vw-2rem))] overflow-hidden rounded border border-border-strong bg-bg-2 text-left shadow-2xl shadow-black/35 transition-colors hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal-ring"
    >
      <span className="flex items-start gap-3 px-3.5 py-3">
        <span
          className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded border ${
            isDone
              ? "border-signal bg-signal text-white"
              : "border-border-strong bg-surface-2 text-signal-text-tint"
          }`}
          aria-hidden="true"
        >
          {isDone ? (
            <FaCheck size={12} />
          ) : batch.phase === "uploading" || batch.phase === "analyzing" ? (
            <FaSpinner className="animate-spin" size={12} />
          ) : (
            <FaUpload size={12} />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center justify-between gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-fg-dim">
              Batch upload
            </span>
            <FaChevronRight
              className="text-fg-dim transition-transform group-hover:translate-x-0.5"
              size={10}
            />
          </span>
          <span className="mt-1 block text-[13px] font-medium text-fg">{summary.label}</span>
          <span className="mt-0.5 block text-[11px] text-fg-muted">{summary.detail}</span>
        </span>
      </span>
      <span className="block h-1 bg-surface-2" aria-hidden="true">
        <span
          className="block h-full bg-signal transition-[width] duration-300"
          style={{ width: `${summary.percent}%` }}
        />
      </span>
    </button>
  );
};
